import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, interval, Subscription } from 'rxjs';
import { STORAGE_KEYS, APP_CONFIG } from '../../../shared/constants';

/**
 * Monitora o token no localStorage e notifica quando muda — permite que logout numa
 * aba propague pras outras (senão elas usariam o token em memória até tomar 401).
 * Ligado em `app.component.ts`.
 */
@Injectable({
  providedIn: 'root'
})
export class TokenMonitorService implements OnDestroy {
  private tokenSubject = new BehaviorSubject<string | null>(null);
  public token$: Observable<string | null> = this.tokenSubject.asObservable();
  private monitoringSubscription?: Subscription;
  private readonly MONITOR_INTERVAL = APP_CONFIG.TOKEN_MONITOR_INTERVAL_MS;

  constructor() {
    const initialToken = localStorage.getItem(STORAGE_KEYS.BRIDGE_TOKEN);
    if (initialToken) {
      this.tokenSubject.next(initialToken);
    }
    this.startMonitoring();
  }

  ngOnDestroy(): void {
    this.stopMonitoring();
  }

  private startMonitoring(): void {
    this.checkToken();
    this.monitoringSubscription = interval(this.MONITOR_INTERVAL).subscribe(() => {
      this.checkToken();
    });
    window.addEventListener('storage', this.handleStorageEvent.bind(this));
  }

  private stopMonitoring(): void {
    if (this.monitoringSubscription) {
      this.monitoringSubscription.unsubscribe();
    }
    window.removeEventListener('storage', this.handleStorageEvent.bind(this));
  }

  private checkToken(): void {
    const currentToken = localStorage.getItem(STORAGE_KEYS.BRIDGE_TOKEN);
    const lastToken = this.tokenSubject.value;

    // Notifica também quando o token não mudou de valor (OAuth2 pode renovar a
    // validade mantendo o mesmo token).
    if (currentToken !== lastToken) {
      this.tokenSubject.next(currentToken);
    } else if (currentToken && !lastToken) {
      this.tokenSubject.next(currentToken);
    }
  }

  private handleStorageEvent(event: StorageEvent): void {
    if (event.key === STORAGE_KEYS.BRIDGE_TOKEN) {
      this.checkToken();
    }
  }

  getCurrentToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.BRIDGE_TOKEN);
  }
}
